const getEffectiveBaseUrl = () => {
    // 1. Detection: Are we in a browser?
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        const port = window.location.port;
        const finalPort = port ? `:${port}` : '';

        // 2. Production Logic: If NOT localhost, always use current origin
        if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
            return `${protocol}//${hostname}${finalPort}`;
        }
    }

    // 3. Fallback/Dev: Use .env if available
    if (process.env.REACT_APP_BASE_URL) return process.env.REACT_APP_BASE_URL.replace(/\/$/, "");
    
    return 'http://localhost:5005';
};

export const BASE_URL = getEffectiveBaseUrl();

// Adaptive API URL: Prioritizes valid production origins over stale .env IPs
const getEffectiveApiUrl = () => {
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        const port = window.location.port;
        
        // If we are on the public IP, assume the backend is reachable via the same host
        // We'll try to preserve the port from .env IF it seems to be a backend port
        if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
            const envApiUrl = process.env.REACT_APP_API_URL;
            if (envApiUrl && envApiUrl.includes('10.5.0.20')) {
                 // The .env has the internal IP, so we MUST override it with current host
                 const protocol = window.location.protocol;
                 const envPort = envApiUrl.split(':').pop().split('/')[0];
                 // Use current hostname but keep the specialized backend port from .env if it exists
                 const targetPort = envPort && !isNaN(envPort) ? `:${envPort}` : '';
                 return `${protocol}//${hostname}${targetPort}/api`;
            }
            return `${BASE_URL}/api`;
        }
    }
    return (process.env.REACT_APP_API_URL || `${BASE_URL}/api`).replace(/\/$/, "");
};

export const API_URL = getEffectiveApiUrl();

// legacy IP normalization (handles both old server and potential port mismatches)
const OLD_IP = "203.193.148.46";

/**
 * Normalizes absolute URLs pointing to old server IPs
 * and ensures relative paths are prefixed with BASE_URL.
 */
export const normalizeUrl = (url) => {
    if (!url) return null;
    if (typeof url !== 'string') return url; // Handle cases where asset imports are used directly

    let normalized = url;

    if (url.startsWith('http') || url.startsWith('data:')) {
        // Replace old IP (with any port) with current BASE_URL
        const oldIpPattern = new RegExp(`https?://${OLD_IP}(:\\d+)?`, 'g');
        if (oldIpPattern.test(url)) {
            normalized = url.replace(oldIpPattern, BASE_URL);
        }
    } else if (url.startsWith('/uploads/') || url.startsWith('uploads/')) {
        const cleanPath = url.startsWith('/') ? url : `/${url}`;
        normalized = `${BASE_URL}${cleanPath}`;
    } else if (url.startsWith('/static/') || url.startsWith('static/')) {
        // Bundled static assets should not be prefixed with BASE_URL if BASE_URL points to a different port
        // Unless they are served by the same backend. Assuming standard React build for now.
        return url;
    }

    return normalized;
};

/**
 * Global Response Handler
 * Ensures that HTTP errors (404, 500, etc.) are thrown as exceptions
 * so they can be caught by the UI catch blocks.
 */
/**
 * System-Wide Dynamic Localizer
 * Automatically swaps _hi fields for main fields if language is Hindi.
 */
const localizeData = (data) => {
    if (!data) return data;
    const lang = localStorage.getItem('site_language') || 'English';
    const suffix = lang === 'Hindi' ? '_hi' : '_en';

    if (Array.isArray(data)) {
        return data.map(item => localizeData(item));
    }

    if (typeof data === 'object' && data !== null) {
        const localized = { ...data };
        const fields = [
            'title', 'name', 'designation', 'text', 'question', 'answer',
            'description', 'content', 'heading', 'label', 'sub_title',
            'subTitle', 'sub_label', 'subLabel', 'message', 'subject', 'department',
            'details', 'soft_color', 'softColor'
        ];

        fields.forEach(f => {
            const val = data[`${f}${suffix}`];
            if (val !== undefined) {
                localized[f] = val;
            }
        });

        // Recursively localize children or other objects
        Object.keys(localized).forEach(key => {
            if (localized[key] && typeof localized[key] === 'object' && key !== 'details_en' && key !== 'details_hi') {
                localized[key] = localizeData(localized[key]);
            }
        });

        return localized;
    }
    return data;
};

const handleResponse = async (res) => {
    if (!res.ok) {
        // 401 Unauthorized: Session Token has likely expired
        if (res.status === 401 && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('VSEZ_SESSION_EXPIRED'));
        }

        let errorData;
        try {
            errorData = await res.json();
        } catch (e) {
            errorData = { message: `System Error (${res.status}): Please check backend logs.` };
        }
        throw new Error(errorData.message || errorData.error || 'Internal Server Error');
    }
    const data = await res.json();
    return localizeData(data);
};

const getHeaders = (token) => {
    return {
        'Content-Type': 'application/json',
        'X-Client-Timestamp': new Date().toISOString(),
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

// Auth
export const loginApi = async (username, password) => {
    const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ username, password })
    });
    return handleResponse(res);
};

export const forgotPasswordApi = async (identifier) => {
    const res = await fetch(`${API_URL}/forgot-password`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ identifier })
    });
    return handleResponse(res);
};

export const resetPasswordApi = async (identifier, token, newPassword) => {
    const res = await fetch(`${API_URL}/reset-password`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ identifier, token, newPassword })
    });
    return handleResponse(res);
};

// GET requests are now cache-busted with a timestamp to ensure fresh data after mutations
export const fetchUsersApi = async (token) => {
    const res = await fetch(`${API_URL}/users?_=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(res);
};

export const createUserApi = async (userData, token) => {
    const res = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
    });
    return handleResponse(res);
};

export const deleteUserApi = async (id, token) => {
    const res = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return handleResponse(res);
};

export const updateUserApi = async (id, userData, token) => {
    const res = await fetch(`${API_URL}/users/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
    });
    return handleResponse(res);
};

// Pages
export const fetchPages = async () => {
    const res = await fetch(`${API_URL}/pages?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchNavigation = async () => {
    const res = await fetch(`${API_URL}/navigation?_=${Date.now()}`);
    return handleResponse(res);
};

export const saveNavItem = async (navData, token) => {
    const res = await fetch(`${API_URL}/navigation`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(navData)
    });
    return handleResponse(res);
};

export const deleteNavItem = async (id, token) => {
    const res = await fetch(`${API_URL}/navigation/${id}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return handleResponse(res);
};

export const fetchPageBySlug = async (slug) => {
    const res = await fetch(`${API_URL}/pages/${slug}?_=${Date.now()}`);
    if (!res.ok) throw new Error('Page not found');
    return handleResponse(res);
};

export const savePageComponent = async (compData, token) => {
    const res = await fetch(`${API_URL}/page-components`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(compData)
    });
    return handleResponse(res);
};

export const uploadFileApi = async (file, title, category, token) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('category', category);

    const res = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });
    return handleResponse(res);
};

export const deletePageComponent = async (id, token) => {
    const res = await fetch(`${API_URL}/page-components/${id}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    return handleResponse(res);
};

export const savePageApi = async (pageData, token) => {
    const method = pageData.id ? 'PUT' : 'POST';
    const url = pageData.id ? `${API_URL}/pages/${pageData.id}` : `${API_URL}/pages`;

    const res = await fetch(url, {
        method,
        headers: getHeaders(token),
        body: JSON.stringify(pageData)
    });
    return handleResponse(res);
};

export const deletePageApi = async (id, token) => {
    const res = await fetch(`${API_URL}/pages/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};

// Site Config / Dynamic Content
export const fetchMinisters = async () => {
    const res = await fetch(`${API_URL}/ministers?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchHeroSlides = async () => {
    const res = await fetch(`${API_URL}/hero?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchNews = async () => {
    const res = await fetch(`${API_URL}/news?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchQuickLinks = async () => {
    const res = await fetch(`${API_URL}/quick-links?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchHighlights = async () => {
    const res = await fetch(`${API_URL}/highlights?_=${Date.now()}`);
    return handleResponse(res);
};

// Save operations for Home Content
export const saveMinisterApi = async (data, token) => {
    // If data is FormData, it's a new upload or update with image
    const isFormData = data instanceof FormData;
    const ministerId = isFormData ? data.get('id') : data.id;

    const url = ministerId ? `${API_URL}/ministers/${ministerId}` : `${API_URL}/ministers`;
    const res = await fetch(url, {
        method: ministerId ? 'PUT' : 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            ...(!isFormData && { 'Content-Type': 'application/json' })
        },
        body: isFormData ? data : JSON.stringify(data)
    });
    return handleResponse(res);
};

export const saveHeroSlideApi = async (data, token) => {
    const url = data.id ? `${API_URL}/hero/${data.id}` : `${API_URL}/hero`;
    const res = await fetch(url, {
        method: data.id ? 'PUT' : 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const syncHeroSlidesApi = async (slides, token) => {
    const res = await fetch(`${API_URL}/hero/all`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(slides)
    });
    return handleResponse(res);
};

export const syncNewsApi = async (newsArr, token) => {
    const url = `${API_URL}/news/all`;
    const res = await fetch(url, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(newsArr)
    });
    return handleResponse(res);
};

export const saveNewsApi = async (data, token) => {
    const url = data.id ? `${API_URL}/news/${data.id}` : `${API_URL}/news`;
    const res = await fetch(url, {
        method: data.id ? 'PUT' : 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const syncQuickLinksApi = async (links, token) => {
    const res = await fetch(`${API_URL}/quick-links/sync`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(links)
    });
    return handleResponse(res);
};

export const saveHighlightApi = async (data, token) => {
    const url = data.id ? `${API_URL}/highlights/${data.id}` : `${API_URL}/highlights`;
    const res = await fetch(url, {
        method: data.id ? 'PUT' : 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteMinisterApi = async (id, token) => {
    const res = await fetch(`${API_URL}/ministers/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    return handleResponse(res);
};

export const deleteHeroSlideApi = async (id, token) => {
    const res = await fetch(`${API_URL}/hero/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    return handleResponse(res);
};

export const deleteNewsApi = async (id, token) => {
    const res = await fetch(`${API_URL}/news/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    return handleResponse(res);
};

export const deleteQuickLinkApi = async (id, token) => {
    const res = await fetch(`${API_URL}/quick-links/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    return handleResponse(res);
};

export const deleteHighlightApi = async (id, token) => {
    const res = await fetch(`${API_URL}/highlights/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    return handleResponse(res);
};

// Home Sections & Photos
export const fetchHomeSections = async () => {
    const res = await fetch(`${API_URL}/home-sections?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchUnits = async (type) => {
    const url = type ? `${API_URL}/units?type=${type}&_=${Date.now()}` : `${API_URL}/units?_=${Date.now()}`;
    const res = await fetch(url);
    return handleResponse(res);
};

export const fetchUnitStats = async () => {
    const res = await fetch(`${API_URL}/units/stats?_=${Date.now()}`);
    return handleResponse(res);
};

export const updateUnitStatsApi = async (id, data, token) => {
    const res = await fetch(`${API_URL}/units/stats/${id}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const saveUnitApi = async (data, token) => {
    const res = await fetch(`${API_URL}/units`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const updateUnitApi = async (id, data, token) => {
    const res = await fetch(`${API_URL}/units/${id}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteUnitApi = async (id, token) => {
    const res = await fetch(`${API_URL}/units/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};

// Private SEZ
export const fetchPrivateSezStats = async () => {
    const res = await fetch(`${API_URL}/private-sez/stats?_=${Date.now()}`);
    return handleResponse(res);
};

// TS SEZ (Telangana SEZ Registry)
export const fetchTsSezStats = async () => {
    const res = await fetch(`${API_URL}/ts-sez/stats?_=${Date.now()}`);
    return handleResponse(res);
};

export const updateTsSezStatApi = async (id, data, token) => {
    const res = await fetch(`${API_URL}/ts-sez/stats/${id}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const fetchPrivateSezBreakup = async () => {
    const res = await fetch(`${API_URL}/private-sez/breakup?_=${Date.now()}`);
    return handleResponse(res);
};

export const savePrivateSezBreakupApi = async (data, token) => {
    const res = await fetch(`${API_URL}/private-sez/breakup`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const updatePrivateSezBreakupApi = async (id, data, token) => {
    const res = await fetch(`${API_URL}/private-sez/breakup/${id}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deletePrivateSezBreakupApi = async (id, token) => {
    const res = await fetch(`${API_URL}/private-sez/breakup/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};

export const updatePrivateSezStatApi = async (id, data, token) => {
    const res = await fetch(`${API_URL}/private-sez/stats/${id}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const saveHomeSectionApi = async (data, token) => {
    const isFormData = data instanceof FormData;
    const res = await fetch(`${API_URL}/home-sections`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            ...(!isFormData && { 'Content-Type': 'application/json' })
        },
        body: isFormData ? data : JSON.stringify(data)
    });
    return handleResponse(res);
};

export const fetchPhotos = async () => {
    const res = await fetch(`${API_URL}/photos?_=${Date.now()}`);
    return handleResponse(res);
};

export const savePhotoApi = async (formData, token) => {
    const res = await fetch(`${API_URL}/photos`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });
    return handleResponse(res);
};

export const updatePhotoApi = async (id, formData, token) => {
    const res = await fetch(`${API_URL}/photos/${id}`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });
    return handleResponse(res);
};

export const deletePhotoApi = async (id, token) => {
    const res = await fetch(`${API_URL}/photos/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    return handleResponse(res);
};

export const incrementVisitorCount = async () => {
    const res = await fetch(`${API_URL}/stats/visit`);
    return handleResponse(res);
};

export const updateVisitorCountApi = async (count, token) => {
    const res = await fetch(`${API_URL}/stats/visit/update`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify({ count })
    });
    return handleResponse(res);
};

// Officers / Directory
export const fetchOfficers = async () => {
    const res = await fetch(`${API_URL}/officers?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchOfficersByCategory = async (category) => {
    const res = await fetch(`${API_URL}/officers/category/${category}?_=${Date.now()}`);
    return handleResponse(res);
};

export const saveOfficerApi = async (data, token) => {
    const method = data.id ? 'PUT' : 'POST';
    const url = data.id ? `${API_URL}/officers/${data.id}` : `${API_URL}/officers`;
    const res = await fetch(url, {
        method,
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteOfficerApi = async (id, token) => {
    const res = await fetch(`${API_URL}/officers/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};

// Documents
export const fetchDocuments = async (page = 1, limit = 20) => {
    const res = await fetch(`${API_URL}/documents?page=${page}&limit=${limit}&_=${Date.now()}`);
    const result = await res.json();
    const data = result.data || result;
    return localizeData(Array.isArray(data) ? data : []);
};

export const fetchDocumentsByCategory = async (category, page = 1) => {
    const res = await fetch(`${API_URL}/documents/category/${category}?page=${page}&_=${Date.now()}`);
    const result = await res.json();
    const data = result.data || result;
    return localizeData(Array.isArray(data) ? data : []);
};

export const fetchLatestDocuments = async (limit = 20) => {
    const res = await fetch(`${API_URL}/documents?limit=${limit}&_=${Date.now()}`);
    return handleResponse(res);
};

export const updateDocumentApi = async (id, formData, token) => {
    try {
        const response = await fetch(`${API_URL}/documents/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`
                // Note: Don't set Content-Type for FormData
            },
            body: formData
        });
        if (!response.ok) throw new Error('Failed to update document');
        const data = await response.json();
        return localizeData(data);
    } catch (err) {
        throw err;
    }
};

export const saveDocumentApi = async (formData, token) => {
    const res = await fetch(`${API_URL}/documents`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
            // Note: Don't set Content-Type, let the browser set it for FormData
        },
        body: formData
    });
    return handleResponse(res);
};

export const deleteDocumentApi = async (id, token) => {
    const res = await fetch(`${API_URL}/documents/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};

// FAQs
export const fetchFaqs = async () => {
    const res = await fetch(`${API_URL}/faqs?_=${Date.now()}`);
    return handleResponse(res);
};

export const fetchFaqsByCategory = async (category) => {
    const res = await fetch(`${API_URL}/faqs/category/${category}?_=${Date.now()}`);
    return handleResponse(res);
};

export const saveFaqApi = async (data, token) => {
    const method = data.id ? 'PUT' : 'POST';
    const url = data.id ? `${API_URL}/faqs/${data.id}` : `${API_URL}/faqs`;
    const res = await fetch(url, {
        method,
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteFaqApi = async (id, token) => {
    const res = await fetch(`${API_URL}/faqs/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};

// Events / Banners
export const fetchEvents = async (token) => {
    const res = await fetch(`${API_URL}/events?_=${Date.now()}`, {
        headers: getHeaders(token)
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to fetch events');
    }
    return handleResponse(res);
};

export const fetchActiveEvents = async () => {
    const res = await fetch(`${API_URL}/events/active?_=${Date.now()}`);
    if (!res.ok) return []; // Return empty on error for public display
    return handleResponse(res);
};

export const saveEventApi = async (data, token) => {
    const isFormData = data instanceof FormData;
    const res = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            ...(!isFormData && { 'Content-Type': 'application/json' })
        },
        body: isFormData ? data : JSON.stringify(data)
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to save event');
    }
    return handleResponse(res);
};

export const deleteEventApi = async (id, token) => {
    const res = await fetch(`${API_URL}/events/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to delete event');
    }
    return handleResponse(res);
};
// Videos
export const fetchVideos = async () => {
    const res = await fetch(`${API_URL}/videos?_=${Date.now()}`);
    return handleResponse(res);
};

export const saveVideoApi = async (formData, token) => {
    const res = await fetch(`${API_URL}/videos`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });
    return handleResponse(res);
};

export const updateVideoApi = async (id, formData, token) => {
    const res = await fetch(`${API_URL}/videos/${id}`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });
    return handleResponse(res);
};

export const deleteVideoApi = async (id, token) => {
    const res = await fetch(`${API_URL}/videos/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    return handleResponse(res);
};

// Feedback
export const submitFeedbackApi = async (formData) => {
    const res = await fetch(`${API_URL}/feedback`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
    });
    return handleResponse(res);
};

// News Media (Press Releases / Clips)
export const fetchNewsMedia = async (type) => {
    const url = type ? `${API_URL}/news-media/type/${type}?_=${Date.now()}` : `${API_URL}/news-media?_=${Date.now()}`;
    const res = await fetch(url);
    return handleResponse(res);
};

export const saveNewsMediaApi = async (formData, token) => {
    const res = await fetch(`${API_URL}/news-media`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });
    return handleResponse(res);
};

export const updateNewsMediaApi = async (id, formData, token) => {
    const res = await fetch(`${API_URL}/news-media/${id}`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData
    });
    const data = await res.json();
    return localizeData(data);
};

export const deleteNewsMediaApi = async (id, token) => {
    const res = await fetch(`${API_URL}/news-media/${id}`, { method: 'DELETE', headers: getHeaders(token) });
    const data = await res.json();
    return localizeData(data);
};

// Infrastructure Assets & Stats
export const fetchInfrastructureData = async () => {
    const res = await fetch(`${API_URL}/infrastructure/data?_=${Date.now()}`);
    return handleResponse(res);
};

export const saveInfrastructureAssetApi = async (data, token) => {
    const res = await fetch(`${API_URL}/infrastructure/asset`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteInfrastructureAssetApi = async (id, token) => {
    const res = await fetch(`${API_URL}/infrastructure/asset/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};

export const saveInfrastructureStatApi = async (data, token) => {
    const res = await fetch(`${API_URL}/infrastructure/stat`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteInfrastructureStatApi = async (id, token) => {
    const res = await fetch(`${API_URL}/infrastructure/stat/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    return handleResponse(res);
};
